import { zodResolver } from "@hookform/resolvers/zod";
import { ArrowLeft, ShieldCheck } from "lucide-react";
import { useForm } from "react-hook-form";
import { z } from "zod";
import { Button } from "@/components/ui/button";
import {
  Form,
  FormControl,
  FormDescription,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/components/ui/form";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import type { SubmitMetadata } from "@/hooks/use-submit-skill";
import { ALL_CATEGORIES } from "@/lib/skills/categories";
import { categoryMeta } from "@/lib/skills/constants";
import { initials } from "@/lib/skills/format";
import { cn } from "@/lib/utils";

const metadataSchema = z.object({
  title: z.string().trim().min(1, "Title is required").max(120),
  desc: z.string().trim().min(1, "A short description is required").max(280),
  categories: z
    .array(z.enum(["UI/UX", "Security", "Efficiency"]))
    .min(1, "Pick at least one category"),
  whatIs: z.string().trim().min(1, "Required").max(4000),
  whyImportant: z.string().trim().min(1, "Required").max(4000),
  example: z.string().trim().min(1, "Required").max(4000),
  install: z.string().trim().min(1, "Required").max(500),
  notes: z.string().trim().max(500).optional(),
  version: z.string().trim().min(1, "Required").max(40),
});
type MetadataValues = z.infer<typeof metadataSchema>;

export function MetadataStep({
  defaultValues,
  submitterName,
  onBack,
  onSubmit,
}: {
  defaultValues: SubmitMetadata | null;
  submitterName: string;
  onBack: () => void;
  onSubmit: (values: SubmitMetadata) => void;
}) {
  const form = useForm<MetadataValues>({
    resolver: zodResolver(metadataSchema),
    defaultValues: defaultValues ?? {
      title: "",
      desc: "",
      categories: [],
      whatIs: "",
      whyImportant: "",
      example: "",
      install: "claude skill add ",
      notes: "",
      version: "1.0.0",
    },
  });

  return (
    <Form {...form}>
      <form
        onSubmit={form.handleSubmit((v) => onSubmit({ ...v, notes: v.notes || undefined }))}
        className="flex flex-col gap-5"
      >
        <FormField
          control={form.control}
          name="title"
          render={({ field }) => (
            <FormItem>
              <FormLabel>Title</FormLabel>
              <FormControl>
                <Input placeholder="e.g. Git Commit Composer" {...field} />
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />

        <FormField
          control={form.control}
          name="desc"
          render={({ field }) => (
            <FormItem>
              <FormLabel>Short description</FormLabel>
              <FormDescription>One line shown on the skill card.</FormDescription>
              <FormControl>
                <Input
                  placeholder="Writes clear commit messages from your staged diff."
                  {...field}
                />
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />

        <FormField
          control={form.control}
          name="categories"
          render={({ field }) => (
            <FormItem>
              <FormLabel>Categories</FormLabel>
              <FormDescription>Assign at least one.</FormDescription>
              <div className="flex flex-wrap gap-2">
                {ALL_CATEGORIES.map((c) => {
                  const on = field.value.includes(c);
                  const Icon = categoryMeta(c).icon;
                  return (
                    <button
                      key={c}
                      type="button"
                      aria-pressed={on}
                      onClick={() =>
                        field.onChange(
                          on ? field.value.filter((x) => x !== c) : [...field.value, c],
                        )
                      }
                      className={cn(
                        "inline-flex items-center gap-1.5 rounded-full border px-3 py-1.5 text-sm font-medium transition-colors",
                        on
                          ? "border-fg-link bg-bg-2 text-fg-1"
                          : "border-border-2 text-fg-2 hover:bg-bg-2",
                      )}
                    >
                      <Icon className="size-4" />
                      {c}
                    </button>
                  );
                })}
              </div>
              <FormMessage />
            </FormItem>
          )}
        />

        <div className="flex items-center gap-2 rounded-[9px] border border-border-1 bg-bg-2 px-4 py-3">
          <span className="grid size-7 place-items-center rounded-full bg-[image:var(--nymbl-gradient)] text-[10px] font-bold text-white">
            {initials(submitterName)}
          </span>
          <span className="text-sm text-fg-2">
            Submitting as <b className="text-fg-1">{submitterName}</b> — you'll be set as author and
            owner.
          </span>
        </div>

        <FormField
          control={form.control}
          name="whatIs"
          render={({ field }) => (
            <FormItem>
              <FormLabel>What is this skill?</FormLabel>
              <FormControl>
                <Textarea
                  className="min-h-[90px]"
                  placeholder="Explain in plain language what the skill does."
                  {...field}
                />
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />

        <FormField
          control={form.control}
          name="whyImportant"
          render={({ field }) => (
            <FormItem>
              <FormLabel>Why is this important?</FormLabel>
              <FormControl>
                <Textarea
                  className="min-h-[90px]"
                  placeholder="What problem does it solve? What's the value?"
                  {...field}
                />
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />

        <FormField
          control={form.control}
          name="example"
          render={({ field }) => (
            <FormItem>
              <FormLabel>Example</FormLabel>
              <FormDescription>A concrete scenario where the skill is useful.</FormDescription>
              <FormControl>
                <Textarea
                  className="min-h-[80px]"
                  placeholder="When committing a feature, stage your changes and run it…"
                  {...field}
                />
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />

        <FormField
          control={form.control}
          name="install"
          render={({ field }) => (
            <FormItem>
              <FormLabel>Install command</FormLabel>
              <FormControl>
                <Input className="font-mono" {...field} />
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />

        <FormField
          control={form.control}
          name="version"
          render={({ field }) => (
            <FormItem>
              <FormLabel>Version</FormLabel>
              <FormControl>
                <Input className="font-mono" placeholder="1.0.0" {...field} />
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />

        <FormField
          control={form.control}
          name="notes"
          render={({ field }) => (
            <FormItem>
              <FormLabel>
                Custom install notes <span className="font-normal text-fg-3">(optional)</span>
              </FormLabel>
              <FormControl>
                <Input placeholder="Any special setup or prerequisites." {...field} />
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />

        <div className="flex justify-between">
          <Button type="button" variant="ghost" onClick={onBack}>
            <ArrowLeft className="size-4" />
            Back
          </Button>
          <Button type="submit">
            Run scan &amp; publish
            <ShieldCheck className="size-4" />
          </Button>
        </div>
      </form>
    </Form>
  );
}
